// Pulled from columbus and modified, need to convert to funtional component later

import React from 'react';
import PropTypes from 'prop-types';
import _uniq from 'lodash/uniq';
import { CarrierCard, ZeroState } from '@itds/components';
import {
  Button,
  Checkbox,
  Input,
  FieldGroup,
  Field,
  Loader,
  Modal,
  Select
} from '@itds/elements/index.js';
import { FontSizes } from '@itds/Theme';
import { FIRELIGHT_ACTIVITIES } from '@itds/Constants';

import { Api } from '@brokerage/utils/Api';

class EditQuoteActionModal extends React.Component {
  static propTypes = {
    actionTypes: PropTypes.array.isRequired,
    agency: PropTypes.object,
    availableCarrierIds: PropTypes.array,
    availableProductIds: PropTypes.array,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    quoteAction: PropTypes.object,
    showOpenInModal: PropTypes.bool
  };

  constructor(props) {
    super(props);

    const quote_action = props.quoteAction || {
      label: '',
      action_type: 'simple_url',
      carrier_ids: [],
      product_ids: [],
      action_data: {
        activity: 'e_app'
      }
    };

    this.state = {
      // action fields
      action_type: quote_action.action_type,
      carrier_ids: quote_action.carrier_ids,
      error_msg: quote_action.error_msg,
      label: quote_action.label,
      open_in_modal: quote_action.open_in_modal,
      order: quote_action.order,
      product_ids: quote_action.product_ids,
      success_msg: quote_action.success_msg,
      url: quote_action.url,

      // data
      carriers: [],
      filtered_carriers: [],
      firelight_carrier_ids: [],
      firelight_product_ids: [],
      firelight_products: [],

      // ui related
      errors: [],
      firelight_activity: quote_action.action_data ? quote_action.action_data.activity : 'e_app',
      firelight_loaded: false,
      firelight_error: false,
      loading_carriers: true
    };
  }

  componentDidMount() {
    this.loadCarriersData();
  }

  componentDidUpdate(prevProps, prevState) {
    // Handle action type changes
    if (prevState.action_type !== this.state.action_type) {
      this.handleActionTypeChange();
    }

    // Handle quote action prop changes
    if (prevProps.quoteAction !== this.props.quoteAction) {
      this.handleQuoteActionChange();
    }
  }

  loadCarriersData = async () => {
    const { availableCarrierIds, availableProductIds, quoteAction } = this.props;

    try {
      const response = await Api.loadAnnuityCarriers();
      const carriers = response.data || [];

      const filteredCarriers = carriers.filter(c => availableCarrierIds?.includes(c.id));

      // Calculate initial IDs based on quote action or default to all
      const initialCarrierIds = quoteAction
        ? (quoteAction.carrier_ids || []).filter(id => availableCarrierIds?.includes(id))
        : filteredCarriers.map(c => c.id);

      const initialProductIds = quoteAction
        ? (quoteAction.product_ids || []).filter(id => availableProductIds?.includes(id))
        : filteredCarriers
            .flatMap(c => c.products?.map(p => p.id) || [])
            .filter(id => availableProductIds?.includes(id));

      this.setState(
        {
          loading_carriers: false,
          carriers: filteredCarriers,
          filtered_carriers: filteredCarriers,
          carrier_ids: initialCarrierIds,
          product_ids: initialProductIds,
          quote_action: quoteAction
        },
        () => {
          // Load firelight products after state is set
          if (this.state.action_type === 'firelight') {
            this._getFirelightProducts();
          } else {
            this._handleFilterCarriers();
          }
        }
      );
    } catch (error) {
      this.setState({ loading_carriers: false });
    }
  };

  handleActionTypeChange = () => {
    // Use requestAnimationFrame to ensure state has been updated
    requestAnimationFrame(() => {
      if (this.state.action_type === 'firelight') {
        this._getFirelightProducts();
      } else {
        this._handleFilterCarriers();
      }
    });
  };

  handleQuoteActionChange = () => {
    const { quoteAction, availableCarrierIds, availableProductIds } = this.props;

    if (quoteAction) {
      this.setState(
        prevState => ({
          carrier_ids: (quoteAction.carrier_ids || []).filter(id =>
            availableCarrierIds?.includes(id)
          ),
          product_ids: (quoteAction.product_ids || []).filter(id =>
            availableProductIds?.includes(id)
          ),
          quote_action: quoteAction
        }),
        () => {
          this._handleFilterCarriers();
        }
      );
    }
  };

  _getFirelightProducts = async () => {
    const { agency, availableCarrierIds, availableProductIds } = this.props;

    if (!agency?.id) {
      return;
    }

    // Prevent multiple concurrent requests
    if (this.state.loading_carriers) {
      return;
    }

    this.setState({
      loading_carriers: true,
      firelight_error: false,
      firelight_loaded: false
    });

    try {
      const { data } = await Api.loadFirelightProducts(
        agency.id,
        this.state.firelight_activity || 'e_app'
      );

      let firelight_product_ids = [];

      if (availableProductIds && data) {
        firelight_product_ids = data.map(p => p.id).filter(id => availableProductIds.includes(id));
      }

      const firelight_carrier_ids = data
        ? _uniq(data.map(p => p.carrier_id).filter(id => availableCarrierIds?.includes(id)))
        : [];

      this.setState(
        prevState => ({
          firelight_product_ids,
          firelight_carrier_ids,
          firelight_products: data || [],
          firelight_error: false,
          firelight_loaded: true,
          product_ids: Array.from(new Set([...prevState.product_ids, ...firelight_product_ids])),
          loading_carriers: false
        }),
        () => {
          // Filter carriers after state update is complete
          this._handleFilterCarriers();
        }
      );
    } catch (error) {
      this.setState({
        firelight_product_ids: [],
        firelight_carrier_ids: [],
        firelight_products: [],
        firelight_error: true,
        firelight_loaded: true,
        loading_carriers: false
      });
    }
  };

  _handleSaveClick = () => {
    const { quoteAction } = this.props;
    const {
      carrier_ids,
      action_type,
      error_msg,
      firelight_activity,
      label,
      open_in_modal,
      order,
      product_ids,
      success_msg,
      url
    } = this.state;
    const errors = [];

    if (!label) {
      errors.push('label');
    }

    if (!carrier_ids.length) {
      errors.push('carrier_ids');
    }

    this.setState({ errors });

    if (!errors.length) {
      const params = {
        id: quoteAction ? quoteAction.id : null,
        action_type,
        carrier_ids,
        error_msg,
        label,
        open_in_modal,
        order,
        product_ids,
        success_msg,
        url
      };

      if (action_type === 'firelight') {
        params.action_data = { activity: firelight_activity };
      }
      this.props.onSave(params);
    }
  };

  _handleSaveProducts = product_ids => {
    this.setState({
      product_ids
    });
  };

  _handleFieldChange = (name, e) => {
    this.setState({
      [name]: e.target.value
    });
  };

  _handleCheckboxChange = (name, e) => {
    this.setState({
      [name]: e.target.checked
    });
  };

  _handleActionTypeChange = e => {
    this.setState({
      action_type: e.target.value
    });
  };

  _handleCarrierToggle = (carrier, checked) => {
    const is_selected = !checked;
    const carrier_product_ids = carrier.products ? carrier.products.map(p => p.id) : [];

    this.setState(prevState => {
      let newCarrierIds = [...prevState.carrier_ids];
      let newProductIds = [...prevState.product_ids];

      if (is_selected) {
        // Add carrier and its products
        if (!newCarrierIds.includes(carrier.id)) {
          newCarrierIds.push(carrier.id);
        }
        carrier_product_ids.forEach(productId => {
          if (!newProductIds.includes(productId)) {
            newProductIds.push(productId);
          }
        });
      } else {
        // Remove carrier and its products
        newCarrierIds = newCarrierIds.filter(id => id !== carrier.id);
        newProductIds = newProductIds.filter(id => !carrier_product_ids.includes(id));
      }

      return {
        carrier_ids: newCarrierIds,
        product_ids: newProductIds
      };
    });
  };

  _handleToggleAll = () => {
    this.setState(prevState => {
      const hasSelectedCarriers = prevState.carrier_ids.length > 0;

      if (hasSelectedCarriers) {
        // Deselect all
        return {
          carrier_ids: [],
          product_ids: []
        };
      } else {
        // Select all filtered carriers
        const allCarrierIds = prevState.filtered_carriers.map(c => c.id);
        const allProductIds = prevState.filtered_carriers.flatMap(c =>
          c.products ? c.products.map(p => p.id) : []
        );

        return {
          carrier_ids: allCarrierIds,
          product_ids: allProductIds
        };
      }
    });
  };

  _handleFilterCarriers = () => {
    const {
      action_type,
      carriers = [],
      firelight_carrier_ids = [],
      firelight_loaded = false
    } = this.state;

    // For firelight, if we haven't loaded firelight products yet, don't filter
    if (action_type === 'firelight' && !firelight_loaded) {
      return;
    }

    const is_firelight = action_type === 'firelight';

    let filtered_carriers;

    if (is_firelight) {
      // For firelight, filter by firelight_carrier_ids
      filtered_carriers = carriers.filter(c => firelight_carrier_ids.includes(c.id));
    } else {
      // For other action types, use all carriers
      filtered_carriers = carriers;
    }

    this.setState(prevState => {
      // For firelight, preserve existing carrier_ids if they're still valid
      let newCarrierIds;

      if (action_type === 'firelight') {
        // Keep existing selections that are still in filtered carriers
        const validExistingIds = prevState.carrier_ids.filter(id =>
          filtered_carriers.some(fc => fc.id === id)
        );
        // If we have valid existing selections, keep them; otherwise select all
        newCarrierIds =
          validExistingIds.length > 0 ? validExistingIds : filtered_carriers.map(fc => fc.id);
      } else {
        // For non-firelight actions, select all filtered carriers
        newCarrierIds = filtered_carriers.map(fc => fc.id);
      }

      return {
        filtered_carriers,
        carrier_ids: newCarrierIds
      };
    });
  };

  render() {
    const {
      action_type,
      carrier_ids,
      error_msg,
      label,
      open_in_modal,
      order,
      product_ids,
      success_msg,
      url,

      firelight_activity,
      firelight_product_ids,

      errors,
      firelight_error,
      firelight_loaded,
      loading_carriers
    } = this.state;
    const { actionTypes, agency, onClose, quoteAction, showOpenInModal } = this.props;
    const styles = this.styles();
    const is_firelight = action_type === 'firelight';
    const is_webhook_post = action_type === 'webhook_post';
    const firelight_enabled = agency.fl_environment;

    const buttons =
      is_firelight && !firelight_enabled
        ? []
        : [
            {
              disabled: !carrier_ids.length,
              children: 'Save',
              onClick: this._handleSaveClick.bind(null, this.state.filtered_carriers),
              style: { marginLeft: 'auto' }
            }
          ];

    return (
      <Modal
        buttons={buttons}
        maxWidth={1400}
        onClose={onClose}
        title={quoteAction ? 'Editing Quote Action' : 'New Quote Action'}
      >
        <div style={styles.modal_content}>
          <div style={styles.fields}>
            <Field label='Type'>
              <Select
                onChange={this._handleActionTypeChange}
                optionGroups={actionTypes}
                value={action_type}
              />
            </Field>

            <div style={{ display: 'flex' }}>
              <div style={{ marginRight: 10, flex: 1 }}>
                <Field label='Label'>
                  <Input
                    invalid={errors.includes('label')}
                    onChange={this._handleFieldChange.bind(null, 'label')}
                    required={true}
                    value={label}
                  />
                </Field>
              </div>

              <div style={{ width: 100 }}>
                <Field label='Display Order'>
                  <Input
                    onChange={this._handleFieldChange.bind(null, 'order')}
                    placeholder='eg: 1'
                    type='number'
                    value={order}
                  />
                </Field>
              </div>
            </div>

            {is_firelight ? null : (
              <>
                <Field label='URL'>
                  <Input onChange={this._handleFieldChange.bind(null, 'url')} value={url} />
                </Field>
              </>
            )}

            {showOpenInModal && !is_firelight ? (
              <div style={{ marginBottom: 20 }}>
                <Checkbox
                  checked={open_in_modal}
                  label='Open inside quoter popup modal'
                  name='open_in_modal'
                  onChange={this._handleCheckboxChange.bind(null, 'open_in_modal')}
                  type='checkbox'
                />
              </div>
            ) : null}

            {is_webhook_post ? (
              <>
                <Field label='Success Message'>
                  <Input
                    onChange={this._handleFieldChange.bind(null, 'success_msg')}
                    type='textarea'
                    value={success_msg}
                  />
                </Field>
                <Field label='Error Message'>
                  <Input
                    onChange={this._handleFieldChange.bind(null, 'error_msg')}
                    type='textarea'
                    value={error_msg}
                  />
                </Field>
              </>
            ) : null}

            {is_firelight && firelight_enabled ? (
              <FieldGroup contained={true} label='FireLight Configuration'>
                <Field label='Activity'>
                  <Select
                    onChange={this._handleFieldChange.bind(null, 'firelight_activity')}
                    options={[
                      { value: '', label: '-- Select One --' },
                      ...FIRELIGHT_ACTIVITIES,
                      { value: 'illustration', label: 'Illustration' }
                    ]}
                    value={firelight_activity || 'e_app'}
                  />
                </Field>
                <Button onClick={this._getFirelightProducts} style={{ width: '100%' }}>
                  Load Available Carriers
                </Button>
              </FieldGroup>
            ) : null}
          </div>

          <div style={styles.content}>
            {loading_carriers ? (
              <Loader />
            ) : (
              <>
                {is_firelight ? (
                  <>
                    {firelight_enabled ? (
                      <>
                        {firelight_loaded ? (
                          <>
                            {firelight_error ? (
                              <ZeroState icon='alert' title='Connection Error' type='error'>
                                We weren’t able to connect to FireLight with the information you
                                provided. Please double check your FireLight integration
                                configuration details located here and try again. If you continue to
                                have problems, please contact us at support@ixn.tech.
                              </ZeroState>
                            ) : (
                              <>
                                {this.state.firelight_products.length ? (
                                  <div style={styles.carriers_panel}>
                                    <Button
                                      inline={true}
                                      onClick={this._handleToggleAll}
                                      style={styles.button}
                                    >
                                      {carrier_ids.length ? 'Select None' : 'Select All'}
                                    </Button>
                                    <div style={styles.carriers}>
                                      {this.state.filtered_carriers.map(carrier => {
                                        const is_selected = carrier_ids.includes(carrier.id);
                                        const products = carrier.products.filter(
                                          p =>
                                            p.cusip && this.props.availableProductIds.includes(p.id)
                                        );

                                        return (
                                          <CarrierCard
                                            carrier={carrier}
                                            isSelected={is_selected}
                                            key={carrier.id}
                                            lineOfBusiness='annuities'
                                            onClick={this._handleCarrierToggle.bind(
                                              null,
                                              carrier,
                                              is_selected
                                            )}
                                            onProductsSave={this._handleSaveProducts}
                                            products={products}
                                            selectedProductIds={product_ids}
                                          />
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : (
                                  <ZeroState
                                    icon='folder-open-outline'
                                    title='No Available Carriers'
                                  >
                                    There are no carriers that support this quote action
                                    configuration.
                                  </ZeroState>
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          <ZeroState icon='folder-open-outline' title='No Carriers Loaded'>
                            Select an Activity to the left and click “Load Available Carriers”
                          </ZeroState>
                        )}
                      </>
                    ) : (
                      <div
                        style={{
                          fontSize: FontSizes.REGULAR,
                          lineHeight: '1.6em',
                          textAlign: 'center',
                          padding: '40px 60px 0'
                        }}
                      >
                        <img
                          alt='Firelight Logo'
                          src='/img/logos/firelight.png'
                          style={{ width: 200, marginBottom: 20 }}
                        />
                        <div style={{ fontStyle: 'italic', marginBottom: 40 }}>
                          FireLight® is a multi-carrier, uniquely flexible electronic application,
                          order entry sales solution that manages the application data collection
                          and submission process, generating in-good-order business processing.
                          Interested in learning more? Contact us at sales@ixn.tech.
                        </div>
                        <div style={{ fontWeight: 600 }}>
                          To enable this quote action, you must first configurate Firelight under
                          the Agency Integrations page. After it’s been configured you can return to
                          this screen to set up the quote action.
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={styles.carriers_panel}>
                    <Button inline={true} onClick={this._handleToggleAll} style={styles.button}>
                      {carrier_ids.length ? 'Select None' : 'Select All'}
                    </Button>

                    <div style={styles.carriers}>
                      {this.state.filtered_carriers.map(carrier => {
                        const is_selected = carrier_ids.includes(carrier.id);

                        let products = carrier.products.filter(p =>
                          this.props.availableProductIds.includes(p.id)
                        );

                        if (is_firelight) {
                          products = products.filter(p => firelight_product_ids.includes(p.id));
                        }

                        return (
                          <CarrierCard
                            carrier={carrier}
                            isSelected={is_selected}
                            key={carrier.id}
                            lineOfBusiness='annuities'
                            onClick={this._handleCarrierToggle.bind(null, carrier, is_selected)}
                            onProductsSave={this._handleSaveProducts}
                            products={products}
                            selectedProductIds={product_ids}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  styles = () => {
    return {
      modal_content: {
        display: 'flex',
        justifyContent: 'space-between',
        overflow: 'hidden'
      },

      fields: {
        width: 400,
        flexShrink: 0,
        padding: 40,
        height: 'calc(100vh - 260px)',
        overflow: 'auto'
      },

      content: {
        overflow: 'auto',
        width: 'calc(100vw - 400px)',
        height: 'calc(100vh - 260px)'
      },
      carriers_panel: {
        padding: '40px 30px'
      },
      carriers: {
        display: 'flex',
        flexWrap: 'wrap'
      },

      button: {
        marginBottom: 20,
        marginLeft: 10
      }
    };
  };
}

export default EditQuoteActionModal;
